import { Component, EventEmitter, Inject, Input, Optional, Output } from '@angular/core';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { Recipient } from '../../../interfaces/recipient';
import { Donor } from '../../../interfaces/donor';
import { CommonModule, KeyValue } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { AuthService } from '../../../auth/auth.service';
import { Permission } from '../../../interfaces/permission';
import { PairService } from '../../../services/pair.service';
import { MatchingPeriodService } from '../../../services/matchingPeriod.service';
import { Pair, ReasonInactive } from '../../../interfaces/pair';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RemoveDialogComponent } from '../../pair/remove-dialog/remove-dialog.component';
import { PairEditComponent } from '../../pair/pair-edit/pair-edit.component';
import { ParticipantService } from '../../../services/participant.service';
import { ConfirmDialogComponent } from '../../../shell/warning-chips/confirm-dialog/confirm-dialog.component';
import { RouterLink } from '@angular/router';
import { combineLatest } from 'rxjs';
import { Approval, ApprovalColumn, ApprovalStatus } from '../../../interfaces/approval';
import { ReportTransplantDialogComponent } from '../report-transplant-dialog/report-transplant-dialog.component';

@Component({
  selector: 'app-participant-box',
  imports: [MatIconModule, CommonModule, MatExpansionModule, MatMenuModule, MatTooltipModule, RouterLink, MatButtonModule],
  templateUrl: './participant-box.component.html',
  styleUrl: './participant-box.component.scss'
})
export class ParticipantBoxComponent {
  //Recipient is a superset of donor, so we can just use recipient
  @Input() recipient?: Recipient;
  @Input() donor?: Donor;
  @Input() pair?: Pair;
  @Input() isMultiDonor?: boolean; // Flag to indicate if this is part of a multi-donor scenario
  @Input() recipientFromGroup?: Recipient; // The recipient from the group (for checking banner logic)

  @Output() pairUpdated = new EventEmitter<Pair>();
  @Output() pairDeleted = new EventEmitter<Pair>();
  @Output() donorUpdated = new EventEmitter<Donor>();
  @Output() recipientUpdated = new EventEmitter<Recipient>();

  //Used to add et number to historyboxes
  @Input() history?: boolean;

  // Hide banners (for pair view where banners are shown externally)
  @Input() hideBanners: boolean = false;

  // Participant action permissions
  @Input() canEditParticipant: boolean = false;
  @Input() canSetTransplanted: boolean = false;
  @Input() canRemoveParticipant: boolean = false;
  @Input() canAddPriorityParticipant: boolean = false;
  @Input() canAddPrivilegesParticipant: boolean = false;
  @Input() canCenterUserEditParticipant: boolean = false;
  @Input() canCenterUserArchiveParticipant: boolean = false;

  // Participant action event emitters
  @Output() editParticipantClicked = new EventEmitter<Donor | Recipient>();
  @Output() removeParticipantClicked = new EventEmitter<Donor | Recipient>();
  @Output() requestPriorityClicked = new EventEmitter<Recipient>();
  @Output() requestPrivilegeClicked = new EventEmitter<Recipient>();
  @Output() requestPrivilegeDonorClicked = new EventEmitter<Donor>();
  canAddPair: boolean = false;
  canEditPair: boolean = false;
  canRemovePair: boolean = false;
  canInactivate: boolean = false;
  isCenterUser: boolean = false;
  userCenterName: string | null = null;
  hasMatchrunStarted: boolean = false;
  donorRecipientId?: string; // Store recipient ID for donors that are part of a pair

  constructor(
    private matIconRegistry: MatIconRegistry,
    private readonly authService: AuthService,
    private pairService: PairService,
    private matchingPeriodService: MatchingPeriodService,
    private participantService: ParticipantService,
    private domSanitizer: DomSanitizer,
    private snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<ParticipantBoxComponent>, // Make MatDialogRef optional
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { donor: Donor; recipient: Recipient } | null // Make MAT_DIALOG_DATA optional
  ) {
    // Assign data to component properties
    if (data) {
      this.donor = data.donor;
      this.recipient = data.recipient;
    }
    // Register your SVG icons here
    this.matIconRegistry.addSvgIcon('human-male', this.domSanitizer.bypassSecurityTrustResourceUrl('/human-male.svg'));

    this.matIconRegistry.addSvgIcon('human-female', this.domSanitizer.bypassSecurityTrustResourceUrl('/human-female.svg'));
  }
  ngOnInit(): void {
    // Combine all async checks to apply restrictions properly
    combineLatest([
      this.authService.getUser(),
      this.authService.getAllPermissions$(),
      this.matchingPeriodService.getLatestMatchingPeriod()
    ]).subscribe(([user, permissions, period]) => {
      // Determine if user is CENTER
      this.isCenterUser = user.activeRole.enumName === 'CENTER';
      this.userCenterName = user.centerName;

      // Check if at least one matchrun has started
      this.hasMatchrunStarted = !!(period?.matchRuns && period.matchRuns.length > 0);

      // If viewing historical participants, disable all edit actions
      if (this.history) {
        this.canAddPair = false;
        this.canEditPair = false;
        this.canRemovePair = false;
        this.canInactivate = false;
      } else {
        this.canAddPair = permissions.includes(Permission.PAIRS_LINK_PARTICIPANTS);
        this.canEditPair = permissions.includes(Permission.PAIRS_EDIT);
        this.canRemovePair = permissions.includes(Permission.PAIRS_EDIT);
        this.canInactivate = permissions.includes(Permission.PAIRS_INACTIVATE);

        // Apply CENTER user restrictions after first matchrun starts
        if (this.isCenterUser && this.hasMatchrunStarted) {
          this.canEditPair = false;
          this.canRemovePair = false;
          this.canInactivate = false;
          // canAddPair stays as is - they can still link participants
        }
      }
    });

    // If donor is provided but no pair or linkedRecipient, fetch the recipient ID
    if (this.donor && !this.pair && !(this.donor as any).linkedRecipient) {
      this.pairService.getAllPairs().subscribe(pairs => {
        const donorPair = pairs.find(p => p.donor.id === this.donor?.id);
        if (donorPair) {
          this.donorRecipientId = donorPair.recipient.id;
        }
      });
    }
  }
  get isRecipient(): boolean {
    return !!this.recipient;
  }

  // Check if donor inactive banner should be shown (only if donor has different reason than recipient)
  shouldShowDonorInactiveBanner(): boolean {
    if (!this.pair || this.pair.active || !this.isMultiDonor) {
      return false;
    }

    // If recipient is active, show banner for all inactive donors (no group banner exists)
    if (this.recipientFromGroup?.active !== false) {
      return true;
    }

    // If recipient is inactive, don't show individual donor banners for system-wide reasons
    // (these are shown at group level instead)
    const systemReasons = ['Awaiting new match run period', 'Not included in minimatch', 'New matching period'];
    if (this.pair.reasonInactive && systemReasons.includes(this.pair.reasonInactive)) {
      return false;
    }

    // Show banner if recipient is inactive but has different reason than the pair
    return this.recipientFromGroup?.reasonInactive !== this.pair.reasonInactive;
  }

  // Check if this specific participant-box should be grayed out (for multi-donor scenarios)
  get shouldGrayOut(): boolean {
    return !!(this.pair && !this.pair.active);
  }
  get age(): number {
    const participantDob = this.recipient?.dob || this.donor?.dob;
    if (participantDob) {
      const birthDate = new Date(participantDob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    }
    return 0; // Default if no DOB is provided
  }

  // Check if participant is locked (either in a locked chain or selected for transplantation)
  get isParticipantLocked(): boolean {
    return !!(
      this.recipient?.locked ||
      this.recipient?.selectedForTransplantation ||
      this.donor?.locked ||
      this.donor?.selectedForTransplantation
    );
  }

  // Get the appropriate lock message based on the participant's status
  get lockMessage(): string {
    const participant = this.recipient || this.donor;
    if (!participant) return 'This participant is locked and cannot be modified';

    const isSelectedForTransplant = !!(participant.selectedForTransplantation);
    const isTransplanted = !!(participant.transplanted);

    if (isSelectedForTransplant && isTransplanted) {
      return 'Transplantation confirmed';
    } else if (isSelectedForTransplant) {
      return 'This participant is selected for transplantation and cannot be modified';
    } else {
      return 'This participant is locked and cannot be modified';
    }
  }

  // Check if donor is locked
  isDonorLocked(): boolean {
    return !!(this.donor?.locked || this.donor?.selectedForTransplantation);
  }

  // Check if recipient is locked
  isRecipientLocked(): boolean {
    return !!(this.recipient?.locked || this.recipient?.selectedForTransplantation);
  }

  // Get donor-specific lock message
  getDonorLockMessage(): string {
    if (!this.donor) return '';

    const isSelectedForTransplant = !!(this.donor.selectedForTransplantation);
    const isTransplanted = !!(this.donor.transplanted);

    if (isSelectedForTransplant && isTransplanted) {
      return 'Donor: Transplantation confirmed';
    } else if (isSelectedForTransplant) {
      return 'Donor: Selected for transplantation';
    } else {
      return 'Donor: Locked';
    }
  }

  // Get recipient-specific lock message
  getRecipientLockMessage(): string {
    if (!this.recipient) return '';

    const isSelectedForTransplant = !!(this.recipient.selectedForTransplantation);
    const isTransplanted = !!(this.recipient.transplanted);

    if (isSelectedForTransplant && isTransplanted) {
      return 'Recipient: Transplantation confirmed';
    } else if (isSelectedForTransplant) {
      return 'Recipient: Selected for transplantation';
    } else {
      return 'Recipient: Locked';
    }
  }

  withdrawPair(pair: Pair, reason: KeyValue<string, ReasonInactive>): void {
    //Set reason and deactivate
    pair.reasonInactive = reason.value;
    pair.active = false;
    this.matchingPeriodService.deactivatePair(pair).subscribe({
      next: (updatedPair) => {
        this.applyPairResponse(updatedPair);

        //this.applyFilters();
        console.log('Pair deactivated:', updatedPair);
        this.showNotification('Pair set to inactive with reason: ' + reason.value);
      },
      error: (err) => {
        console.error('Error editing pair:', err);
        this.showNotification('Failed to set pair inactive. Please try again.');
      }
    });
  }

  getWithdrawReasons(): Record<string, ReasonInactive> {
    // Exclude automatic reasons that should only be set by the system
    return {
      MedicalProblemsPatient: ReasonInactive.MedicalProblemsPatient,
      MedicalProblemsDonor: ReasonInactive.MedicalProblemsDonor,
      PhychologicalProblemsPatient: ReasonInactive.PhychologicalProblemsPatient,
      PhychologicalProblemsDonor: ReasonInactive.PhychologicalProblemsDonor,
      Other: ReasonInactive.Other
    };
  }

  // Get reasons for donors and altruists (exclude patient-related and automatic reasons)
  getDonorWithdrawReasons(): Record<string, ReasonInactive> {
    return {
      MedicalProblemsDonor: ReasonInactive.MedicalProblemsDonor,
      PhychologicalProblemsDonor: ReasonInactive.PhychologicalProblemsDonor,
      Other: ReasonInactive.Other
    };
  }

  // Get reasons for recipients (exclude donor-related and automatic reasons)
  getRecipientWithdrawReasons(): Record<string, ReasonInactive> {
    return {
      MedicalProblemsPatient: ReasonInactive.MedicalProblemsPatient,
      PhychologicalProblemsPatient: ReasonInactive.PhychologicalProblemsPatient,
      Other: ReasonInactive.Other
    };
  }

  removePair(pair: Pair) {
    const dialogRef = this.dialog.open(RemoveDialogComponent, {
      data: pair
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.pair = undefined; // Clear the pair from the component
        this.pairDeleted.emit(pair);
      }
    });
  }

  editPair(pair: Pair) {
    const dialogRef = this.dialog.open(PairEditComponent, {
      data: { currentPair: pair } // Pass the item data here
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (result.action === 'unpair') {
          // Unpair action: clear the pair and emit pairDeleted
          this.pair = undefined;
          this.pairDeleted.emit(pair);
          console.log('Unpaired pair, need to refresh participant links:', result.pairId);
        } else if (result.action === 'edit') {
          // Edit action: update the pair
          this.pair = result.pair;
          this.pairUpdated.emit(result.pair);
          console.log('Updated pair:', result.pair);
        }
      }
    });
  }

  //The difference between activate/deactive pair is that the deactive gives the Pair object to the backend. This is done to add a reason to the inactive version
  activatePair(pair: Pair) {
    // Block activation for pairs awaiting new match run period
    if (pair.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Cannot activate pairs that are awaiting new match run period. Please wait for the next matching period.');
      return;
    }

    // Block center users from activating pairs with "Awaiting new match run period" reason
    if (this.isCenterUser && pair.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Center users cannot activate pairs awaiting new match run period.');
      return;
    }

    // Check if pair has a reason other than 'New matching period'
    if (pair.reasonInactive && pair.reasonInactive !== 'New matching period') {
      // Show confirmation dialog
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          pair: pair,
          message: `This pair is inactive with reason: "${pair.reasonInactive}". Are you sure you want to set it to active?`
        },
        width: '500px'
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.applyPairResponse(result);
          this.showNotification('Pair set to active');
        }
      });
    } else {
      // No confirmation needed, activate directly
      this.matchingPeriodService.activatePair(pair.id).subscribe({
        next: (updatedPair) => {
          this.applyPairResponse(updatedPair);
          this.showNotification('Pair set to active');
        },
        error: (err) => console.error(err)
      });
    }
  }

  changeInactiveReason(pair: Pair, reason: KeyValue<string, ReasonInactive>): void {
    // Block changing reason for pairs awaiting new match run period
    if (pair.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Cannot change inactive reason for pairs awaiting new match run period.');
      return;
    }

    // Block center users from changing inactive reason if current reason is "Awaiting new match run period"
    if (this.isCenterUser && pair.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Center users cannot change inactive reason for pairs awaiting new match run period.');
      return;
    }

    // Change the reason without changing active status
    const updatedPair = { ...pair, reasonInactive: reason.value };
    this.pairService.updatePair(updatedPair).subscribe({
      next: (result) => {
        this.pair = result;
        this.pairUpdated.emit(result);
        this.showNotification('Inactive reason changed to: ' + reason.value);
      },
      error: (err) => {
        console.error('Error changing inactive reason:', err);
        this.showNotification('Failed to change inactive reason. Please try again.');
      }
    });
  }

  deactivatePair(pair: Pair) {
    this.matchingPeriodService.deactivatePair(pair).subscribe({
      next: (updatedPair) => {
        this.applyPairResponse(updatedPair);
      },
      error: (err) => console.error(err)
    });
  }

  activateAltruist(donor: Donor) {
    if (!donor.id) return;

    // Block activation for altruists awaiting new match run period
    if (donor.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Cannot activate participants that are awaiting new match run period. Please wait for the next matching period.');
      return;
    }

    // Block center users from activating altruists with "Awaiting new match run period" reason
    if (this.isCenterUser && donor.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Center users cannot activate participants awaiting new match run period.');
      return;
    }

    // Check if donor has a reason other than 'New matching period'
    if (donor.reasonInactive && donor.reasonInactive !== 'New matching period') {
      // Show confirmation dialog
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          donor: donor,
          message: `This altruist is inactive with reason: "${donor.reasonInactive}". Are you sure you want to set it to active?`
        },
        width: '500px'
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.donor = result;
          this.donorUpdated.emit(result);
          this.showNotification('Altruist set to active');
        }
      });
    } else {
      // No confirmation needed, activate directly
      const updatedDonor = { ...donor, active: true, reasonInactive: undefined };
      this.participantService.updateDonor(updatedDonor).subscribe({
        next: (result) => {
          this.donor = result;
          this.donorUpdated.emit(result);
          this.showNotification('Altruist set to active');
        },
        error: (err) => {
          console.error('Error activating altruist:', err);
          this.showNotification('Failed to activate altruist. Please try again.');
        }
      });
    }
  }

  deactivateAltruist(donor: Donor, reason: KeyValue<string, ReasonInactive>) {
    if (!donor.id) return;

    const updatedDonor = { ...donor, active: false, reasonInactive: reason.value };
    this.participantService.updateDonor(updatedDonor).subscribe({
      next: (result) => {
        this.donor = result;
        this.donorUpdated.emit(result);
        this.showNotification('Altruist set to inactive with reason: ' + reason.value);
      },
      error: (err) => {
        console.error('Error deactivating altruist:', err);
        this.showNotification('Failed to deactivate altruist. Please try again.');
      }
    });
  }

  changeAltruistInactiveReason(donor: Donor, reason: KeyValue<string, ReasonInactive>) {
    if (!donor.id) return;

    // Block changing reason for altruists awaiting new match run period
    if (donor.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Cannot change inactive reason for participants awaiting new match run period.');
      return;
    }

    // Block center users from changing inactive reason if current reason is "Awaiting new match run period"
    if (this.isCenterUser && donor.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Center users cannot change inactive reason for participants awaiting new match run period.');
      return;
    }

    const updatedDonor = { ...donor, reasonInactive: reason.value };
    this.participantService.updateDonor(updatedDonor).subscribe({
      next: (result) => {
        this.donor = result;
        this.donorUpdated.emit(result);
        this.refreshPairAfterParticipantChange();
        this.showNotification('Inactive reason changed to: ' + reason.value);
      },
      error: (err) => {
        console.error('Error changing inactive reason:', err);
        this.showNotification('Failed to change inactive reason. Please try again.');
      }
    });
  }

  activateDonor(donor: Donor) {
    if (!donor.id) return;

    // Block activation for donors awaiting new match run period
    if (donor.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Cannot activate participants that are awaiting new match run period. Please wait for the next matching period.');
      return;
    }

    // Block center users from activating donors with "Awaiting new match run period" reason
    if (this.isCenterUser && donor.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Center users cannot activate participants awaiting new match run period.');
      return;
    }

    // Check if donor has a reason other than 'New matching period'
    if (donor.reasonInactive && donor.reasonInactive !== 'New matching period') {
      // Show confirmation dialog
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          donor: donor,
          message: `This donor is inactive with reason: "${donor.reasonInactive}". Are you sure you want to set it to active?`
        },
        width: '500px'
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.donor = result;
          this.donorUpdated.emit(result);
          this.refreshPairAfterParticipantChange();
          this.showNotification('Donor set to active');
        }
      });
    } else {
      // No confirmation needed, activate directly
      const updatedDonor = { ...donor, active: true, reasonInactive: undefined };
      this.participantService.updateDonor(updatedDonor).subscribe({
        next: (result) => {
          this.donor = result;
          this.donorUpdated.emit(result);
          this.refreshPairAfterParticipantChange();
          this.showNotification('Donor set to active');
        },
        error: (err) => {
          console.error('Error activating donor:', err);
          this.showNotification('Failed to activate donor. Please try again.');
        }
      });
    }
  }

  deactivateDonor(donor: Donor, reason: KeyValue<string, ReasonInactive>) {
    if (!donor.id) return;

    const updatedDonor = { ...donor, active: false, reasonInactive: reason.value };
    this.participantService.updateDonor(updatedDonor).subscribe({
      next: (result) => {
        this.donor = result;
        this.donorUpdated.emit(result);
        this.refreshPairAfterParticipantChange();
        this.showNotification('Donor set to inactive with reason: ' + reason.value);
      },
      error: (err) => {
        console.error('Error deactivating donor:', err);
        this.showNotification('Failed to deactivate donor. Please try again.');
      }
    });
  }

  changeDonorInactiveReason(donor: Donor, reason: KeyValue<string, ReasonInactive>) {
    if (!donor.id) return;

    // Block changing reason for donors awaiting new match run period
    if (donor.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Cannot change inactive reason for participants awaiting new match run period.');
      return;
    }

    // Block center users from changing inactive reason if current reason is "Awaiting new match run period"
    if (this.isCenterUser && donor.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Center users cannot change inactive reason for participants awaiting new match run period.');
      return;
    }

    const updatedDonor = { ...donor, reasonInactive: reason.value };
    this.participantService.updateDonor(updatedDonor).subscribe({
      next: (result) => {
        this.donor = result;
        this.donorUpdated.emit(result);
        this.refreshPairAfterParticipantChange();
        this.showNotification('Inactive reason changed to: ' + reason.value);
      },
      error: (err) => {
        console.error('Error changing inactive reason:', err);
        this.showNotification('Failed to change inactive reason. Please try again.');
      }
    });
  }

  activateRecipient(recipient: Recipient) {
    if (!recipient.id) return;

    // Block activation for recipients awaiting new match run period
    if (recipient.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Cannot activate participants that are awaiting new match run period. Please wait for the next matching period.');
      return;
    }

    // Block center users from activating recipients with "Awaiting new match run period" reason
    if (this.isCenterUser && recipient.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Center users cannot activate participants awaiting new match run period.');
      return;
    }

    // Check if recipient has a reason other than 'New matching period'
    if (recipient.reasonInactive && recipient.reasonInactive !== 'New matching period') {
      // Show confirmation dialog
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          recipient: recipient,
          message: `This recipient is inactive with reason: "${recipient.reasonInactive}". Are you sure you want to set it to active?`
        },
        width: '500px'
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.recipient = result;
          this.recipientUpdated.emit(result);
          this.refreshPairAfterParticipantChange();
          this.showNotification('Recipient set to active');
        }
      });
    } else {
      // No confirmation needed, activate directly
      const updatedRecipient = { ...recipient, active: true, reasonInactive: undefined };
      this.participantService.updateRecipient(updatedRecipient).subscribe({
        next: (result) => {
          this.recipient = result;
          this.recipientUpdated.emit(result);
          this.refreshPairAfterParticipantChange();
          this.showNotification('Recipient set to active');
        },
        error: (err) => {
          console.error('Error activating recipient:', err);
          this.showNotification('Failed to activate recipient. Please try again.');
        }
      });
    }
  }

  deactivateRecipient(recipient: Recipient, reason: KeyValue<string, ReasonInactive>) {
    if (!recipient.id) return;

    const updatedRecipient = { ...recipient, active: false, reasonInactive: reason.value };
    this.participantService.updateRecipient(updatedRecipient).subscribe({
      next: (result) => {
        this.recipient = result;
        this.recipientUpdated.emit(result);
        this.refreshPairAfterParticipantChange();
        this.showNotification('Recipient set to inactive with reason: ' + reason.value);
      },
      error: (err) => {
        console.error('Error deactivating recipient:', err);
        this.showNotification('Failed to deactivate recipient. Please try again.');
      }
    });
  }

  changeRecipientInactiveReason(recipient: Recipient, reason: KeyValue<string, ReasonInactive>) {
    if (!recipient.id) return;

    // Block changing reason for recipients awaiting new match run period
    if (recipient.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Cannot change inactive reason for participants awaiting new match run period.');
      return;
    }

    // Block center users from changing inactive reason if current reason is "Awaiting new match run period"
    if (this.isCenterUser && recipient.reasonInactive === 'Awaiting new match run period') {
      this.showNotification('Center users cannot change inactive reason for participants awaiting new match run period.');
      return;
    }

    const updatedRecipient = { ...recipient, reasonInactive: reason.value };
    this.participantService.updateRecipient(updatedRecipient).subscribe({
      next: (result) => {
        this.recipient = result;
        this.recipientUpdated.emit(result);
        this.refreshPairAfterParticipantChange();
        this.showNotification('Inactive reason changed to: ' + reason.value);
      },
      error: (err) => {
        console.error('Error changing inactive reason:', err);
        this.showNotification('Failed to change inactive reason. Please try again.');
      }
    });
  }

  /**
   * After a participant-level state change the backend may have cascaded it to the pair and to
   * the other participant, so the pair is refetched and mirrored here. Without this the box
   * would keep showing a stale pair/partner state until the next full reload (CROS-398).
   */
  private refreshPairAfterParticipantChange(): void {
    if (!this.pair?.id) return;

    this.pairService.getPair(this.pair.id).subscribe({
      next: (pair) => {
        if (pair) {
          this.applyPairResponse(pair);
        }
      },
      error: (err) => {
        console.error('Error refreshing pair after participant change:', err);
      }
    });
  }

  /**
   * Mirror a pair response from the backend into this box.
   *
   * The backend (PairStateService) owns the active state of the pair AND of both participants
   * and returns all three in one response. The UI must not PUT the participants itself: those
   * extra calls can fail or race and leave the three flags out of sync (CROS-398).
   */
  private applyPairResponse(pair: Pair): void {
    this.pair = pair;
    this.pairUpdated.emit(pair);

    if (pair.donor && this.donor?.id === pair.donor.id) {
      this.donor = pair.donor;
      this.donorUpdated.emit(pair.donor);
    }

    if (pair.recipient && this.recipient?.id === pair.recipient.id) {
      this.recipient = pair.recipient;
      this.recipientUpdated.emit(pair.recipient);
    }
  }

  //This should be a general function accessible using an import, as it's now copied in a lot of seperate components.
  showNotification(message: string, action: string = 'Close'): void {
    this.snackBar.open(message, action, {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }

  /**
   * Get donor HLA Class I (A, B antigens)
   */
  get donorHlaClassI(): string {
    if (!this.donor?.hla) return '';
    const tokens = this.donor.hla.split(' ');
    const drIndex = tokens.findIndex((t) => t.startsWith('DR'));
    if (drIndex !== -1) {
      return tokens.slice(0, drIndex).join(' ');
    }
    return this.donor.hla;
  }

  /**
   * Get donor HLA Class II (DR antigens)
   */
  get donorHlaClassII(): string {
    if (!this.donor?.hla) return '';
    const tokens = this.donor.hla.split(' ');
    const drIndex = tokens.findIndex((t) => t.startsWith('DR'));
    if (drIndex !== -1) {
      return tokens.slice(drIndex).join(' ');
    }
    return '';
  }

  /**
   * Format acceptable blood types as comma-separated for display
   */
  get formattedAcceptableBloodTypes(): string {
    if (!this.recipient?.acceptableBloodTypes) return '';
    return this.recipient.acceptableBloodTypes.replace(/\s+/g, ', ');
  }

  /**
   * Check if recipient is awaiting ENIS sync
   */
  get isRecipientAwaitingEnisSync(): boolean {
    return this.recipient ? (!this.recipient.firstName && !this.recipient.lastName) : false;
  }

  /**
   * Check if donor is awaiting ENIS sync
   */
  get isDonorAwaitingEnisSync(): boolean {
    return this.donor ? (!this.donor.firstName && !this.donor.lastName) : false;
  }

  /**
   * Close the dialog if this component is displayed in one
   */
  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  /**
   * Check if recipient is allowed to have privileges
   */
  isAllowedPrivileges(): boolean {
    if (this.recipient) {
      // PM waitlist patients cannot have privileges
      if (this.recipient.postmortumwaitlist === true) {
        return false;
      }

      // Check if recipient is younger than 18 years
      if (this.recipient.dob) {
        const birthDate = new Date(this.recipient.dob);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        if (age < 18) {
          return true;
        }
      }

      // Check if there's a compatible pair
      if (this.pair && this.pair.compatible) {
        return true;
      }
    } else if (this.donor) {
      // Altruists can have privileges
      if (this.donor.altruist) {
        return true;
      }
    }
    return false;
  }

  /**
   * Emit edit participant clicked
   */
  onEditParticipant(): void {
    const participant = this.recipient || this.donor;
    if (participant) {
      this.editParticipantClicked.emit(participant);
    }
  }

  /**
   * Emit remove participant clicked
   */
  onRemoveParticipant(): void {
    const participant = this.recipient || this.donor;
    if (participant) {
      this.removeParticipantClicked.emit(participant);
    }
  }

  /**
   * Emit request priority clicked
   */
  onRequestPriority(): void {
    if (this.recipient) {
      this.requestPriorityClicked.emit(this.recipient);
    }
  }

  /**
   * Emit request privilege clicked
   */
  onRequestPrivilege(): void {
    if (this.recipient) {
      this.requestPrivilegeClicked.emit(this.recipient);
    }
  }

  /**
   * Emit request privilege donor clicked
   */
  onRequestPrivilegeDonor(): void {
    if (this.donor) {
      this.requestPrivilegeDonorClicked.emit(this.donor);
    }
  }

  /**
   * Check if participant can report transplantation
   * - Must have selectedForTransplantation status (unless CENTER user has PARTICIPANTS_SET_TRANSPLANTED permission)
   * - Must not already be transplanted
   * - Must have either PARTICIPANTS_EDIT or PARTICIPANTS_SET_TRANSPLANTED permission
   * - CENTER users can only report transplantation for participants from their own center
   */
  canReportTransplantation(): boolean {
    const participant = this.recipient || this.donor;
    if (!participant) return false;

    // Must not already be transplanted
    if (participant.transplanted) return false;

    // Must have permission to edit participants or set transplanted
    const hasPermission = this.canEditParticipant || this.canSetTransplanted;
    if (!hasPermission) return false;

    // CENTER users can only report transplantation for participants from their own center
    if (this.isCenterUser && this.userCenterName) {
      const participantCenterName = participant.center?.name;
      if (participantCenterName !== this.userCenterName) {
        return false;
      }

      // CENTER users with PARTICIPANTS_SET_TRANSPLANTED permission can report for any participant from their center
      // BUT only if they are selected for transplantation
      if (this.canSetTransplanted && participant.selectedForTransplantation) return true;
    }

    // For non-CENTER users (or CENTER users without PARTICIPANTS_SET_TRANSPLANTED permission),
    // participant must be selected for transplantation
    return !!participant.selectedForTransplantation;
  }

  /**
   * Open dialog to report transplantation for this participant
   */
  onReportTransplantation(): void {
    const participant = this.recipient || this.donor;
    if (!participant) return;

    const dialogRef = this.dialog.open(ReportTransplantDialogComponent, {
      data: { participant },
      width: '600px'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed) {
        // Format date to YYYY-MM-DD for reportedTransplantDate
        const transplantDate = new Date(result.transplantDate);
        const formattedDate = transplantDate.toISOString().split('T')[0];

        // Update participant with transplantation data (but NOT historical yet)
        const updatedParticipant = {
          ...participant,
          transplanted: true,
          reportedTransplantDate: formattedDate
        };

        // Call appropriate update method
        if (this.recipient) {
          this.participantService.updateRecipient(updatedParticipant).subscribe({
            next: (result) => {
              this.recipient = result;
              this.recipientUpdated.emit(result);
              this.showNotification('Recipient marked as transplanted');

              // Check if paired donor is also transplanted
              this.checkAndSetPairHistorical();
            },
            error: (err) => {
              console.error('Error reporting recipient transplantation:', err);
              this.showNotification('Failed to report transplantation. Please try again.');
            }
          });
        } else if (this.donor) {
          this.participantService.updateDonor(updatedParticipant).subscribe({
            next: (result) => {
              this.donor = result;
              this.donorUpdated.emit(result);
              this.showNotification('Donor marked as transplanted');

              // Check if paired recipient is also transplanted
              this.checkAndSetPairHistorical();
            },
            error: (err) => {
              console.error('Error reporting donor transplantation:', err);
              this.showNotification('Failed to report transplantation. Please try again.');
            }
          });
        }
      }
    });
  }

  /**
   * Check if both pair members are transplanted, and if so, set both to historical
   * and set the pair to historical
   */
  private checkAndSetPairHistorical(): void {
    // Check if we have a pair
    if (!this.pair) return;

    const donor = this.donor;
    const recipient = this.recipient;

    // For a donor-recipient pair view
    if (donor && recipient) {
      // Both participants must be transplanted
      if (donor.transplanted && recipient.transplanted) {
        this.setParticipantsAndPairHistorical(donor, recipient);
      }
    }
    // For donor-only view (check linked recipient)
    else if (donor && donor.linkedRecipient) {
      if (donor.transplanted && donor.linkedRecipient.transplanted) {
        this.setParticipantsAndPairHistorical(donor, donor.linkedRecipient);
      }
    }
    // For recipient-only view (check first linked donor)
    else if (recipient && recipient.linkedDonors && recipient.linkedDonors.length > 0) {
      const linkedDonor = recipient.linkedDonors[0];
      if (recipient.transplanted && linkedDonor.transplanted) {
        this.setParticipantsAndPairHistorical(linkedDonor, recipient);
      }
    }
  }

  /**
   * Set both participants and their pair to historical
   */
  private setParticipantsAndPairHistorical(donor: Donor, recipient: Recipient): void {
    const now = new Date().toISOString();
    const historicalReason = 'Transplanted within cross-over program';

    // Update donor to historical
    const updatedDonor = {
      ...donor,
      historical: true,
      historicalDate: now,
      historicalReason: historicalReason
    };

    // Update recipient to historical
    const updatedRecipient = {
      ...recipient,
      historical: true,
      historicalDate: now,
      historicalReason: historicalReason
    };

    // Update both participants
    this.participantService.updateDonor(updatedDonor).subscribe({
      next: (result) => {
        if (this.donor) {
          this.donor = result;
          this.donorUpdated.emit(result);
        }
      },
      error: (err) => {
        console.error('Error setting donor to historical:', err);
      }
    });

    this.participantService.updateRecipient(updatedRecipient).subscribe({
      next: (result) => {
        if (this.recipient) {
          this.recipient = result;
          this.recipientUpdated.emit(result);
        }
      },
      error: (err) => {
        console.error('Error setting recipient to historical:', err);
      }
    });

    // Update pair to historical if we have it
    if (this.pair && this.pair.id) {
      const updatedPair = {
        ...this.pair,
        historical: true,
        historicalDate: now,
        historicalReason: historicalReason
      };

      this.pairService.updatePair(updatedPair).subscribe({
        next: (result) => {
          this.pair = result;
          this.pairUpdated.emit(result);
          this.showNotification('Both pair members confirmed - pair set to historical');
        },
        error: (err) => {
          console.error('Error setting pair to historical:', err);
        }
      });
    }
  }
}
